// Tema claro/escuro do painel. O `themeState` e a unica fonte de verdade no JS: componente
// que precisa saber o tema le daqui, nunca de `classList.contains('dark')`.
//   - `theme`: a escolha do usuario. Muda NO CLIQUE (o icone do botao reage na hora).
//   - `appliedTheme`: o que ja esta na tela. Muda quando a classe `dark` e aplicada, e
//     e o que quem le token (graficos) deve observar.
// As cores em si vivem em src/utils/styles/theme.scss.
import Vue from 'vue';
import themeManager, { THEME_DARK, THEME_LIGHT } from '@/utils/managers/ThemeManager';

const DARK_CLASS = 'dark';
const SWITCHING_CLASS = 'theme-switching';

// Cor da barra do navegador no mobile, igual ao --bg-app de cada tema
const BROWSER_BAR_COLOR: { [theme: string]: string } = {
  [THEME_LIGHT]: '#f9f9fa',
  [THEME_DARK]: '#100c12',
};

const initialTheme = themeManager.getTheme();

export const themeState = Vue.observable({
  theme: initialTheme,
  appliedTheme: initialTheme,
});

let switchId = 0;

export function isDarkTheme(): boolean {
  return themeState.theme === THEME_DARK;
}

// Roda `callback` depois que o navegador PINTOU o estado atual. E o que garante que o
// botao responde no frame do clique, antes do trabalho pesado de trocar o tema da pagina.
function afterNextPaint(callback: () => void): void {
  if (document.hidden) {
    // aba de fundo nao pinta (nem roda rAF): aplica direto
    callback();
    return;
  }

  window.requestAnimationFrame(() => window.setTimeout(callback, 0));
}

function applyTheme(theme: string): void {
  const root = document.documentElement;

  // Desliga as transicoes durante a troca: sem isso cada componente anima a sua cor
  // num ritmo diferente e a troca parece "escorrer" pela tela.
  root.classList.add(SWITCHING_CLASS);
  root.classList.toggle(DARK_CLASS, theme === THEME_DARK);

  const metas = document.querySelectorAll('meta[name="theme-color"]');
  for (let i = 0; i < metas.length; i++) {
    metas[i].setAttribute('content', BROWSER_BAR_COLOR[theme]);
  }

  // Forca o recalculo de estilo com as transicoes desligadas e so entao religa.
  // (setTimeout e nao requestAnimationFrame: rAF nao roda em aba de fundo.)
  window.getComputedStyle(root).getPropertyValue('opacity');
  window.setTimeout(() => root.classList.remove(SWITCHING_CLASS), 1);

  themeState.appliedTheme = theme;
}

export function setTheme(theme: string): void {
  const next = theme === THEME_DARK ? THEME_DARK : THEME_LIGHT;

  // 1. Feedback imediato: o icone do botao troca neste frame
  themeState.theme = next;
  // Mesmo sem storage (modo privado) a troca vale para a sessao atual
  themeManager.setTheme(next);

  // 2. A pagina troca depois que esse frame foi pintado. Clique repetido antes disso:
  //    so a ultima escolha e aplicada.
  const id = ++switchId;

  afterNextPaint(() => {
    if (id === switchId) {
      applyTheme(next);
    }
  });
}

export function toggleTheme(): void {
  setTheme(isDarkTheme() ? THEME_LIGHT : THEME_DARK);
}

// Chamado no boot (main.ts). O script inline de public/index.html ja aplicou a classe
// antes do primeiro paint; aqui so sincroniza o estado e a meta theme-color.
export function initTheme(): void {
  applyTheme(themeState.theme);
}
